import { FIELDS_TYPES } from "constants/fieldsTypes";
import {
  SurveyAnswerOption,
  SurveyData,
  SurveyLanguage,
} from "./types";

export const getQuestionFieldName = (
  answerTypeUUID: string,
  questionUUID: string,
  groupUUID = ""
) => `${answerTypeUUID},${questionUUID},${groupUUID}`;

export const DEFAULT_SURVEY_LANGUAGE: SurveyLanguage = "ua";

export const SURVEY_LANGUAGES: SurveyLanguage[] = ["ua", "ru", "en"];

export const SURVEY_LANGUAGE_OPTIONS: Array<{
  value: SurveyLanguage;
  label: string;
}> = [
  { value: "ua", label: "Українська" },
  { value: "ru", label: "Русский" },
  { value: "en", label: "English" },
];

export const SURVEY_TEXTS: Record<
  SurveyLanguage,
  {
    languageLabel: string;
    loading: string;
    requiredMessage: string;
    selectPlaceholder: string;
    submit: string;
    defaultErrorMessage: string;
    defaultSuccessMessage: string;
    successDescription: string;
    surveyUnavailable: string;
    refresh: string;
  }
> = {
  ua: {
    languageLabel: "Мова",
    loading: "Завантаження",
    requiredMessage: "Заповніть це поле",
    selectPlaceholder: "Оберіть відповідь",
    submit: "Відправити",
    defaultErrorMessage: "Помилка. Спробуйте пізніше.",
    defaultSuccessMessage: "Ваш відгук успішно отримано.",
    successDescription: "Дякуємо за приділений час.",
    surveyUnavailable: "Опитування недоступне",
    refresh: "Оновити",
  },
  ru: {
    languageLabel: "Язык",
    loading: "Загрузка",
    requiredMessage: "Заполните это поле",
    selectPlaceholder: "Выберите ответ",
    submit: "Отправить",
    defaultErrorMessage: "Ошибка. Попробуйте позже.",
    defaultSuccessMessage: "Ваш отзыв успешно получен.",
    successDescription: "Спасибо за уделенное время.",
    surveyUnavailable: "Опрос недоступен",
    refresh: "Обновить",
  },
  en: {
    languageLabel: "Language",
    loading: "Loading",
    requiredMessage: "Fill in this field",
    selectPlaceholder: "Select an answer",
    submit: "Submit",
    defaultErrorMessage: "Error. Please try again later.",
    defaultSuccessMessage: "Your feedback has been received.",
    successDescription: "Thank you for your time.",
    surveyUnavailable: "Survey unavailable",
    refresh: "Refresh",
  },
};

export interface LocalizedSelectOption {
  label: string;
  value: string;
}

const BAD_RATING_VALUES = new Set([1, 2, 3]);

const isLocalizedValue = (
  value: unknown
): value is Partial<Record<SurveyLanguage, string | number | null>> => {
  return !!value && typeof value === "object" && !Array.isArray(value);
};

const hasUsableValue = (value: unknown) => {
  return (
    value !== null &&
    value !== undefined &&
    (typeof value !== "string" || value.trim() !== "")
  );
};

const getLocalizedValue = (
  value: unknown,
  language: SurveyLanguage,
  fallback?: unknown
) => {
  if (!isLocalizedValue(value)) {
    return hasUsableValue(value) ? value : fallback;
  }

  const candidates = [
    value[language],
    value[DEFAULT_SURVEY_LANGUAGE],
    fallback,
    ...SURVEY_LANGUAGES.map((item) => value[item]),
  ];

  return candidates.find(hasUsableValue);
};

export const getLocalizedText = (
  value: unknown,
  language: SurveyLanguage,
  fallback = ""
) => {
  const localizedValue = getLocalizedValue(value, language, fallback);

  return hasUsableValue(localizedValue) ? String(localizedValue) : "";
};

export const getLocalizedQuestionText = (
  question: SurveyData.SurveyQuestions,
  language: SurveyLanguage
) => getLocalizedText(question.questionLoc, language, question.question);

export const getLocalizedOptions = (
  options: SurveyAnswerOption[],
  language: SurveyLanguage
): LocalizedSelectOption[] => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.reduce<LocalizedSelectOption[]>((result, item) => {
    const value = getLocalizedValue(item, DEFAULT_SURVEY_LANGUAGE);

    if (!hasUsableValue(value)) {
      return result;
    }

    result.push({
      value: String(value),
      label: getLocalizedText(item, language, String(value)),
    });

    return result;
  }, []);
};

export const hasBadRatingAnswer = (
  groups: SurveyData.Group[],
  values: Record<string, any>
) => {
  return groups.some((group) =>
    group.questions.some((question) => {
      if (
        question.showIfBadRating ||
        question.answerType.answerTypeUUID !== FIELDS_TYPES.SELECT_NUMBER
      ) {
        return false;
      }

      const value = values[
        getQuestionFieldName(
          question.answerType.answerTypeUUID,
          question.questionUUID,
          group.groupUUID
        )
      ];

      const answers = Array.isArray(value) ? value : [value];

      return answers.some((answer) => BAD_RATING_VALUES.has(Number(answer)));
    })
  );
};

export const getVisibleQuestionFieldNames = (
  groups: SurveyData.Group[],
  values: Record<string, any>
) => {
  const shouldShowBadRatingQuestions = hasBadRatingAnswer(groups, values);
  const fieldNames = new Set<string>();

  groups.forEach((group) => {
    group.questions.forEach((question) => {
      if (question.showIfBadRating && !shouldShowBadRatingQuestions) {
        return;
      }

      fieldNames.add(
        getQuestionFieldName(
          question.answerType.answerTypeUUID,
          question.questionUUID,
          group.groupUUID
        )
      );
    });
  });

  return fieldNames;
};
